import { GAP_LABELS } from './constants';

export const DAY_MS = 24 * 60 * 60 * 1000;
export const WEEK_MS = 7 * DAY_MS;

// Never invent more than this many placeholder weeks for a hole in the `weeks` array.
const MAX_FILLED_WEEKS = 110;

export const isGapStatus = (status) => status === 'away' || status === 'break';

export const toTime = (weekStart) => {
  const time = Date.parse(weekStart);
  return Number.isNaN(time) ? null : time;
};

// Valid weeks only (tolerates absent or malformed `weeks` from v1 responses), oldest first,
// one entry per week (a duplicated weekStart keeps the entry with the most sessions).
export const normalizeWeeks = (weeks) => {
  const byTime = new Map();
  (Array.isArray(weeks) ? weeks : []).forEach((w) => {
    const time = toTime(w?.weekStart);
    if (time === null) {
      return;
    }
    const entry = {
      weekStart: w.weekStart,
      time,
      sessions: Number.isFinite(w?.sessions) && w.sessions > 0 ? w.sessions : 0,
      status: w?.status || 'idle',
    };
    const existing = byTime.get(time);
    if (!existing || entry.sessions > existing.sessions) {
      byTime.set(time, entry);
    }
  });
  return [...byTime.values()].sort((a, b) => a.time - b.time);
};

// normalizeWeeks plus a `missing` placeholder for every week absent between two present ones.
export const fillWeekGaps = (weeks) => {
  const list = normalizeWeeks(weeks);
  const filled = [];
  list.forEach((w, index) => {
    const prev = list[index - 1];
    if (prev) {
      const holes = Math.round((w.time - prev.time) / WEEK_MS) - 1;
      for (let k = 1; k <= Math.min(holes, MAX_FILLED_WEEKS); k += 1) {
        const time = prev.time + k * WEEK_MS;
        filled.push({
          weekStart: new Date(time).toISOString().slice(0, 10),
          time,
          sessions: 0,
          status: 'missing',
        });
      }
    }
    filled.push(w);
  });
  return filled;
};

// Consecutive away or break weeks of the same kind merge into one band: [start, end) in ms.
export const getGapBands = (weeks) => {
  const bands = [];
  normalizeWeeks(weeks).forEach((w) => {
    if (!isGapStatus(w.status)) {
      return;
    }
    const last = bands[bands.length - 1];
    if (last && last.status === w.status && last.end >= w.time) {
      last.end = w.time + WEEK_MS;
      last.weeks += 1;
    } else {
      bands.push({ status: w.status, start: w.time, end: w.time + WEEK_MS, weeks: 1 });
    }
  });
  return bands;
};

const countText = (sessions) =>
  sessions > 0 ? `${sessions} ${sessions === 1 ? 'session' : 'sessions'}` : 'no sessions';

export const describeWeek = (week) => {
  const base = `Week of ${week.weekStart}`;
  if (week.status === 'missing') {
    return `${base}: no data`;
  }
  if (isGapStatus(week.status)) {
    const text = `${base}: ${GAP_LABELS[week.status]}, time away from training`;
    return week.sessions > 0 ? `${text}, ${countText(week.sessions)}` : text;
  }
  return `${base}: ${countText(week.sessions)}`;
};
